import { detectGeoColumns } from './geoDetection'

export const AUDIT_TYPES = {
  NUMERIC: 'numeric',
  CATEGORICAL: 'categorical',
  DATETIME: 'date/time',
  BOOLEAN: 'boolean',
  TEXT: 'text',
}

const TYPE_SAMPLE_SIZE = 300
const DUP_SAMPLE_SIZE = 1000

function formatCount(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

const isNullish = (v) => v === null || v === undefined || v === ''
export const tryParseNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const normalized = value.trim().replace(/,/g, '')
  if (!normalized || !/^[-+]?\d*(\.\d+)?$/.test(normalized)) return null
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}
const tryParseDate = (value) => {
  if (typeof value !== 'string') return null
  const d = new Date(value.trim())
  return Number.isNaN(d.getTime()) ? null : d
}
const inferColumnType = (values) => {
  const nonNull = values.filter((v) => !isNullish(v))
  if (!nonNull.length) return AUDIT_TYPES.TEXT
  const pct = (fn) => nonNull.filter(fn).length / nonNull.length
  if (pct((v) => tryParseNumber(v) !== null) > 0.9) return AUDIT_TYPES.NUMERIC
  if (pct((v) => /^(true|false|yes|no|0|1)$/i.test(String(v).trim())) > 0.9) return AUDIT_TYPES.BOOLEAN
  if (pct((v) => tryParseDate(v) !== null) > 0.9) return AUDIT_TYPES.DATETIME
  const uniqueRatio = new Set(nonNull.map(String)).size / nonNull.length
  const avgLen = nonNull.reduce((a, v) => a + String(v).length, 0) / nonNull.length
  return uniqueRatio < 0.8 || avgLen < 20 ? AUDIT_TYPES.CATEGORICAL : AUDIT_TYPES.TEXT
}
const outlierCount = (numbers) => {
  if (numbers.length < 4) return 0
  const sorted = [...numbers].sort((a, b) => a - b)
  const q1 = sorted[Math.floor((sorted.length - 1) * 0.25)]
  const q3 = sorted[Math.floor((sorted.length - 1) * 0.75)]
  const iqr = q3 - q1
  const low = q1 - 1.5 * iqr
  const high = q3 + 1.5 * iqr
  return numbers.filter((n) => n < low || n > high).length
}
export function runDataAudit(rows = [], options = {}) {
  const columns = rows[0] ? Object.keys(rows[0]) : []
  const schema = {}
  const warnings = []
  const totalRows =
    typeof options.totalRows === 'number' && Number.isFinite(options.totalRows)
      ? options.totalRows
      : rows.length
  const sampled = Boolean(options.sampled) && totalRows > rows.length
  const geoCandidates = detectGeoColumns(rows)

  const stride = rows.length > TYPE_SAMPLE_SIZE ? Math.ceil(rows.length / TYPE_SAMPLE_SIZE) : 1
  const sampleRows = stride > 1 ? rows.filter((_, i) => i % stride === 0) : rows

  columns.forEach((column) => {
    const values = rows.map((r) => r[column])
    const sampleValues = stride > 1 ? sampleRows.map((r) => r[column]) : values
    const nonNull = values.filter((v) => !isNullish(v))
    const uniqueValues = new Set(nonNull.map(String)).size
    const type = inferColumnType(sampleValues)
    const missingCount = values.length - nonNull.length
    const likelyMeasure = type === AUDIT_TYPES.NUMERIC
    const likelyId = uniqueValues === nonNull.length && column.toLowerCase().includes('id')
    const tooManyUniques = type === AUDIT_TYPES.CATEGORICAL && uniqueValues > Math.max(20, Math.floor(rows.length * 0.25))
    const sampleNonNull = sampleValues.filter((v) => !isNullish(v))
    const outliers = likelyMeasure ? outlierCount(sampleNonNull.map(tryParseNumber).filter((n) => n !== null)) : 0
    schema[column] = { type, missingCount, uniqueValues, likelyId, likelyMeasure, outlierCount: outliers, tooManyUniques }
    if (missingCount) {
      warnings.push(`${column}: ${formatCount(missingCount, 'blank value')} found.`)
    }
    if (tooManyUniques) {
      warnings.push(
        `${column}: has ${uniqueValues} different values, so it may be too detailed to use as a category.`
      )
    }
    if (outliers) {
      warnings.push(
        `${column}: ${formatCount(outliers, 'unusually high or low value')} worth reviewing.`
      )
    }
  })

  const dupRows = rows.length > DUP_SAMPLE_SIZE ? rows.slice(0, DUP_SAMPLE_SIZE) : rows
  let duplicateRows = 0
  const seen = new Set()
  dupRows.forEach((row) => {
    const key = JSON.stringify(row)
    if (seen.has(key)) duplicateRows += 1
    seen.add(key)
  })
  if (duplicateRows) {
    warnings.push(`${formatCount(duplicateRows, 'duplicate row')} found.`)
  }
  warnings.push(...geoCandidates.warnings)
  return {
    rows: totalRows,
    rowsAnalyzed: rows.length,
    sampled,
    columns: columns.length,
    schema,
    warnings,
    duplicateRows,
    geoCandidates,
    recommendedFixes: [
      'Trim extra spaces',
      'Turn empty cells into blank values',
      'Convert number-like text into numbers',
      'Convert date-like text into dates when the format is clear',
      'Remove fully empty rows',
      'Review unusual values manually',
    ],
  }
}
